import React, { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';

// Data
import { buildCreatePlayerAssessment } from '../../../lib/services/player-assessment.service';

// Components
import { CreatePlayerContainer } from './create-player.container';
import { getAuthUser } from '../../../lib/services/auth.service';
import { buildCreateAgeGroup, getAgeGroups, useRoleAllowedAgeGroups } from '../../../lib/services/age-group.service';
import { getCurrMember } from '../../../lib/services/member.service';
import { providedMemberHasAnyRoles } from '../../../lib/services/user.service';
import { useCompanySport } from '../../../lib/stores/company.store';
import { ROLES } from '../../../../_configuration';

const GET_CREATE_PLAYER = gql`
  query GetCreatePlayer($companyId: ID!, $includeGlobalForSport: SPORT) {
    positions(companyId: $companyId, includeGlobalForSport: $includeGlobalForSport) {
      _id
      name
    }
  }
`;

const CONFIGURATION_ROLES = [ROLES.CONFIGURATION];

interface ICreatePlayerData {
  onComplete?: Function;
  // The roles that let the user add a player to an age group; the check-in page adds CHECK_IN.
  ageGroupRoles?: string[];
  // The check-in page's ice time age group: the only one a player may be created in there.
  ageGroup?: IAgeGroup;
}

export const CreatePlayerData: React.FC<ICreatePlayerData> = ({ onComplete, ageGroupRoles = CONFIGURATION_ROLES, ageGroup }) => {
  const user = getAuthUser();
  const sport = useCompanySport()
  const { member } = getCurrMember();
  const assessmentId = user?.settings.activeAssessmentId;

  const { data, loading, error } = useQuery(GET_CREATE_PLAYER, {
    skip: !user,
    variables: { companyId: user?.settings.activeCompanyId, includeGlobalForSport: sport },
    fetchPolicy: 'cache-and-network'
  });
  const positions = (data && data.positions) || [];

  const { ageGroups: activeAgeGroups } = getAgeGroups({requiredRoles: ageGroupRoles, useActive: true})
  const onlyAgeGroup = useMemo(() => (ageGroup ? [ageGroup] : []), [ageGroup]);
  const allowedOnlyAgeGroup = useRoleAllowedAgeGroups({ ageGroups: onlyAgeGroup, requiredRoles: ageGroupRoles });
  const ageGroups = ageGroup ? allowedOnlyAgeGroup : activeAgeGroups;
  const fixedAgeGroup = ageGroup ? allowedOnlyAgeGroup[0] : undefined;

  const { createAgeGroup } = buildCreateAgeGroup();
  const { createPlayerAssessment } = buildCreatePlayerAssessment();

  // Creating an age group is configuration, even where CHECK_IN may add players to one.
  const canCreateAgeGroup = useMemo(
    () => providedMemberHasAnyRoles({ member, roles: CONFIGURATION_ROLES, assessmentId }),
    [member, assessmentId]
  );

  // A fixed age group is decided by the member's roles, so wait for them: a later remount would wipe typed input.
  if (ageGroup && !member) return null;

  // Keyed so a different fixed age group (another ice time) re-seeds the form.
  return <CreatePlayerContainer
    key={fixedAgeGroup?._id}
    createPlayerMutation={createPlayerAssessment}
    ageGroups={ageGroups}
    fixedAgeGroup={fixedAgeGroup}
    createAgeGroup={canCreateAgeGroup && !ageGroup ? createAgeGroup : undefined}
    positions={positions}
    loading={loading}
    fetchError={Boolean(error)}
  />;
};
